import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "../../../assets/css/SalesTrend/QuaterlyPerformance.css";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useSalesContext } from "../../SalesContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const QuaterlyPerformance = () => {
  const { kpiData, salesUnit, loading, unit } = useSalesContext();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 900);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const defaultQtrPerformance = {};

  if (loading || !kpiData) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  let qtrPerformance =
    kpiData?.kpi_data?.card4_qtr_performance ??
    kpiData?.kpi_data?.Card3 ??
    defaultQtrPerformance;

  const fiscalYears = new Set();
  Object.values(qtrPerformance).forEach((quarter) => {
    Object.keys(quarter).forEach((fy) => fiscalYears.add(fy));
  });
  const sortedFiscalYears = Array.from(fiscalYears).sort();

  const filledQtrPerformance = {};
  Object.keys(qtrPerformance).forEach((quarter) => {
    filledQtrPerformance[quarter] = {};
    sortedFiscalYears.forEach((fy) => {
      filledQtrPerformance[quarter][fy] = qtrPerformance[quarter][fy] || {
        target_value: 0,
        actual_value: 0,
      };
    });
  });

  const groupedLabels = Object.keys(filledQtrPerformance).map((quarter) => ({
    parent: quarter,
    children: Object.keys(filledQtrPerformance[quarter]),
  }));

  const labels = groupedLabels.flatMap((group, index) => [
    ...group.children,
    ...(index < groupedLabels.length - 1 ? [""] : []),
  ]);

  const targetValues = [];
  const actualValues = [];

  let conversionFactor;
  let unitLabel;
  conversionFactor = salesUnit === "Crores" ? 10000000 : 100000;
  unitLabel = salesUnit || "Lacs";

  Object.keys(filledQtrPerformance).forEach((quarter) => {
    const quarterData = filledQtrPerformance[quarter];
    Object.keys(quarterData).forEach((fy) => {
      let targetValue, actualValue;

      if (unit === "Units") {
        targetValue = Number(
          Math.max(
            (quarterData[fy]?.target_value_Qty || 0) / conversionFactor,
            0
          ).toFixed(1)
        );
        actualValue = Number(
          Math.max(
            (quarterData[fy]?.actual_value_Qty || 0) / conversionFactor,
            0
          ).toFixed(1)
        );
      } else {
        targetValue = Number(
          Math.max(
            (quarterData[fy]?.target_value || 0) / conversionFactor,
            0
          ).toFixed(1)
        );
        actualValue = Number(
          Math.max(
            (quarterData[fy]?.actual_value || 0) / conversionFactor,
            0
          ).toFixed(1)
        );
      }

      targetValues.push(targetValue);
      actualValues.push(actualValue);
    });

    if (
      quarter !==
      Object.keys(filledQtrPerformance)[
        Object.keys(filledQtrPerformance).length - 1
      ]
    ) {
      targetValues.push(null);
      actualValues.push(null);
    }
  });

  const data = {
    labels,
    datasets: [
      {
        label: "Target Value",
        data: targetValues,
        backgroundColor: "#D3D3D3",
        borderColor: "#D3D3D3",
        borderWidth: 1,
        barThickness: isSmallScreen ? 20 : 56,
        order: 1,
      },
      {
        label: "Actual Value",
        data: actualValues,
        barThickness: isSmallScreen ? 15 : 34,
        backgroundColor: "lightblue",
        borderColor: "#1a80bb",
        borderWidth: 1,
        categoryPercentage: 0.5,
        order: 0,
      },
    ],
  };

  const parentLabelsPlugin = {
    id: "parentLabels",
    afterDraw: (chart) => {
      const { ctx, scales } = chart;
      const xAxis = scales.x;
      const yAxisBottom = scales.y.bottom;

      ctx.save();
      ctx.textAlign = "center";
      ctx.font = `bold ${isSmallScreen ? 10 : 14}px Arial`;

      ctx.fillStyle = "#1a80bb";
      ctx.letterSpacing = "0.15px";

      const topPadding = 35;
      const labelPosition = yAxisBottom + topPadding + 14;

      let xStart = xAxis.left;
      groupedLabels.forEach((group) => {
        const childCount = group.children.length + 1;
        const xEnd = xStart + (childCount * xAxis.width) / labels.length;
        const xMiddle = (xStart + xEnd - xAxis.width / labels.length) / 2;

        ctx.fillText(group.parent, xMiddle, labelPosition);
        xStart = xEnd;
      });

      ctx.restore();
    },
  };

  const valueLabelsPlugin = {
    id: "valueLabels",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const { datasets } = chart.data;

      ctx.save();
      ctx.font = `bold ${isSmallScreen ? 10 : 14}px Arial`;
      ctx.textAlign = "center";

      datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);

        meta.data.forEach((bar, index) => {
          if (bar && bar.y !== null && dataset.data[index] !== null) {
            const value = dataset.data[index];
            const xPos = bar.x;

            if (dataset.label === "Target Value") {
              const yPos = bar.base - 10;
              ctx.fillStyle = "#000";
              ctx.fillText(value, xPos, yPos);
            } else if (dataset.label === "Actual Value") {
              const yPos = bar.y - 10;
              ctx.fillStyle = "#000";
              ctx.fillText(value, xPos, yPos);
            }
          }
        });
      });

      ctx.restore();
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 32,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          callback: (value, index) => {
            if (labels[index] === "") return "";
            let year = parseInt(labels[index].slice(-2));
            year = (year + 1) % 100;
            return `FY${year.toString().padStart(2, "0")}`;
          },
          font: {
            weight: "700",
            size: isSmallScreen ? 10 : 12,
          },
          color: "#000000",
          lineHeight: 1,
          letterSpacing: "0%",
        },
      },
      y: {
        beginAtZero: true,
        display: false,
      },
    },
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          usePointStyle: false,
          boxWidth: 15,
          boxHeight: 15,
          padding: 10,
          font: {
            size: isSmallScreen ? 12 : 14,
            weight: "bold",
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => ({
              text: dataset.label,
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.borderColor,
              lineWidth: dataset.borderWidth,
              hidden: !chart.isDatasetVisible(i),
              index: i,
              fontColor:
                dataset.label === "Target Value" ? "#808080" : "#1a80bb",
            }));
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        filter: (tooltipItem) => {
          return (
            tooltipItem &&
            tooltipItem.chart.data.labels[tooltipItem.dataIndex] !== ""
          );
        },
        callbacks: {
          title: (tooltipItems) => {
            if (!tooltipItems || !tooltipItems.length) {
              return "";
            }
            const index = tooltipItems[0].dataIndex;
            const label = tooltipItems[0].chart.data.labels[index];
            if (!label) return "";
            let year = parseInt(label.slice(-2));
            year = (year + 1) % 100;
            return `FY${year.toString().padStart(2, "0")}`;
          },
          label: (tooltipItem) => {
            if (!tooltipItem) {
              return [];
            }
            const datasetLabel = tooltipItem.dataset.label;
            const index = tooltipItem.dataIndex;
            const value =
              tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].data[
                index
              ];
            return `${datasetLabel}: ${value} ${unitLabel}`;
          },
        },
      },
    },
    categoryPercentage: 0.8,
    barPercentage: 0.9,
  };

  return (
    <Box className="chart">
      <Box className="header">
        <Box className="title-container">
          <Typography className="title">
            Quarterly Performance ({salesUnit || "Lacs"})
          </Typography>
        </Box>
      </Box>
      <Box className="chartCard">
        <div className="chartBox">
          <Bar
            data={data}
            options={options}
            plugins={[parentLabelsPlugin, valueLabelsPlugin]}
          />
        </div>
      </Box>
    </Box>
  );
};

export default QuaterlyPerformance;
